import { Volume2, PackageOpen, MonitorUp, ShieldCheck, Globe } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const TerminalMock = () => (
  <div className="mt-8 rounded-xl border border-white/10 bg-ink overflow-hidden font-mono text-[13px] leading-7" data-testid="bento-terminal-mock">
    <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/5">
      <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
      <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
      <span className="w-2.5 h-2.5 rounded-full bg-neon/60" />
      <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-dim">no-installs-needed.sh</span>
    </div>
    <div className="p-5">
      <p className="text-mist"><span className="text-neon">$</span> jiggly --install</p>
      <p className="text-dim">→ nothing to install. this is a web page.</p>
      <p className="text-mist"><span className="text-neon">$</span> jiggly --admin-rights</p>
      <p className="text-dim">→ none requested. sandboxed by your browser.</p>
      <p className="text-white">● status: <span className="text-neon">GREEN</span><span className="blink text-neon">_</span></p>
    </div>
  </div>
);

const Waveform = () => (
  <div className="eq-idle mt-8 flex items-end gap-1.5 h-20" data-testid="bento-waveform">
    {Array.from({ length: 22 }).map((_, i) => (
      <span
        key={i}
        className="eq-bar flex-1 rounded-sm bg-neon/70"
        style={{ height: "100%", animationDelay: `${(i % 8) * 0.14}s` }}
      />
    ))}
  </div>
);

const Cell = ({ className, icon: Icon, title, copy, children, testid }) => (
  <FadeUp className={`h-full ${className}`}>
    <div
      data-testid={testid}
      className="h-full rounded-2xl border border-white/5 bg-surface p-7 sm:p-9 transition-colors duration-300 hover:border-neon/30 hover:bg-surface/80"
    >
      <div className="w-11 h-11 rounded-xl border border-neon/25 bg-neon/10 flex items-center justify-center">
        <Icon size={19} className="text-neon" />
      </div>
      <h3 className="mt-6 font-display font-bold text-xl text-white tracking-tight">{title}</h3>
      <p className="mt-3 text-mist text-[15px] leading-relaxed">{copy}</p>
      {children}
    </div>
  </FadeUp>
);

export default function FeaturesBento() {
  return (
    <section className="relative py-24 sm:py-32" data-testid="features-bento-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Cell
            className="md:col-span-7"
            icon={PackageOpen}
            title="Zero installation"
            copy="Runs entirely in your web browser. Corporate IT departments cannot block it the way they block executables — there are no .exe files to download and no admin privileges required."
            testid="bento-cell-zero-install"
          >
            <TerminalMock />
          </Cell>

          <Cell
            className="md:col-span-5"
            icon={Volume2}
            title="Background audio engine"
            copy="A silent background audio loop and the Web Audio API prevent modern browsers from throttling the tab when you switch to other applications."
            testid="bento-cell-audio"
          >
            <Waveform />
          </Cell>

          <Cell
            className="md:col-span-4"
            icon={MonitorUp}
            title="Screen sleep prevention"
            copy="Native Wake Lock API keeps your monitor awake and your status online, even if you walk away."
            testid="bento-cell-wakelock"
          />

          <Cell
            className="md:col-span-4"
            icon={ShieldCheck}
            title="Undetectable by design"
            copy="To IT, Jiggly is just a tab playing a silent video. No registry entries, no processes, no hardware signatures."
            testid="bento-cell-undetectable"
          />

          <Cell
            className="md:col-span-4"
            icon={Globe}
            title="Works everywhere"
            copy="Chrome, Edge, Safari and Firefox — on Windows, macOS, ChromeOS and Linux."
            testid="bento-cell-cross-platform"
          />
        </div>
      </div>
    </section>
  );
}
